import { useEffect, useState } from "react";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { dayLabel, formatFileSize, shortTime } from "./format";
import { Selection } from "./useChatData";
import { DownloadIcon } from "./Icons";
import { downloadAttachment } from "./download";

export const MEDIA_TYPES = [
  CometChat.MESSAGE_TYPE.IMAGE,
  CometChat.MESSAGE_TYPE.VIDEO,
  CometChat.MESSAGE_TYPE.AUDIO,
  CometChat.MESSAGE_TYPE.FILE,
];

/**
 * Every attachment shared in ONE conversation, newest first — the
 * per-chat counterpart to the global Files rail panel. Rendered inside
 * the Details panel, so it's scoped to whichever DM or group is open.
 */
export default function ConversationFiles({ selection }: { selection: Selection }) {
  const [files, setFiles] = useState<CometChat.MediaMessage[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFiles(null);
    setError(false);

    const builder = new CometChat.MessagesRequestBuilder()
      .setLimit(50)
      .setCategories([CometChat.CATEGORY_MESSAGE])
      .setTypes(MEDIA_TYPES);
    if (selection.kind === "group") builder.setGUID(selection.id);
    else builder.setUID(selection.id);

    builder
      .build()
      .fetchPrevious()
      .then((list) => {
        if (cancelled) return;
        const out = ((list as CometChat.BaseMessage[]) || [])
          .filter((m) => MEDIA_TYPES.includes(m.getType()) && !m.getDeletedAt())
          .reverse() as CometChat.MediaMessage[];
        setFiles(out);
      })
      .catch((e) => {
        console.warn("conversation files fetch failed", e);
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [selection.kind, selection.id]);

  return (
    <div className="modal-section">
      <div className="modal-label">Files {files && files.length > 0 && `(${files.length})`}</div>
      <div className="modal-list">
        {files === null && !error && <div className="sect-empty">Loading…</div>}
        {error && <div className="sect-empty">Couldn't load files</div>}
        {files?.length === 0 && <div className="sect-empty">No files shared here yet</div>}
        {files?.map((m) => {
          const attachment = m.getAttachment?.();
          const name = attachment?.getName() || "Attachment";
          const size = formatFileSize(attachment?.getSize());
          const url = attachment?.getUrl();
          return (
            <div className="later-item" key={m.getId()}>
              <a
                className="later-item__main"
                href={url || undefined}
                target="_blank"
                rel="noreferrer"
                title={name}
              >
                <span className="later-item__text">{name}</span>
                <span className="later-item__meta">
                  {m.getSender()?.getName() || "Unknown"}
                  {size ? ` · ${size}` : ""} · {dayLabel(m.getSentAt())} {shortTime(m.getSentAt())}
                </span>
              </a>
              {url && (
                <button
                  className="later-item__remove"
                  title={`Download ${name}`}
                  aria-label={`Download ${name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void downloadAttachment(url, name);
                  }}
                >
                  <DownloadIcon size={16} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
